import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from '../../images/CloseIcon';
import {
  deleteDeviceTender,
  getListDeviceTender,
  updateDeviceTender,
} from '../../services/deviceTender.service';
import PlusIconWhite from '../../images/PlusIconWhite';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import { getLocations } from '../../services/inventoryCorrections.service';
import { useResponsive } from '../../hook/useResponsive';
import DeviceIcon from '../../images/DeviceIcon';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import {
  BORDER_SOFT,
  BottomSheet,
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  TEXT,
  TEXT_DIM,
  TEXT_FAINT,
  cells,
  t,
} from '../../components/list';

interface TenderRow {
  id: string;
  icon: string;
  tenderCode: string;
  tenderType: string;
  tenderName: string;
  requireReference: boolean;
  position: number;
  maxPaymentRestriction?: string | number | null;
  updatedAt: Date;
  isActive: boolean;
  location: { id: string; companyId?: string }[];
}
interface DeviceTenderResponse {
  data: {
    data: {
      deviceTender: TenderRow[];
      count: number;
    };
  };
}
interface LocationOption {
  id: string;
  name: string;
}

// The list returns the enum name; the update DTO takes the number.
const TENDER_TYPES = [
  { label: 'Cash', value: 1 },
  { label: 'Card', value: 2 },
  { label: 'Promotion', value: 3 },
  { label: 'Voucher', value: 4 },
  { label: 'OnAccount', value: 5 },
];

/** Tender icon image; an em-dash when unset or the file fails to load. */
const TenderIcon: React.FC<{ src?: string | null }> = ({ src }) => {
  const [failed, setFailed] = useState<boolean>(false);
  if (!src || failed) return <span style={{ color: TEXT_FAINT }}>—</span>;
  return (
    <img
      src={src}
      alt=""
      style={{ width: '28px', height: '28px', objectFit: 'contain', display: 'block' }}
      onError={() => setFailed(true)}
    />
  );
};

const LocationPinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 19" fill="none">
    <path
      d="M11.9168 8.66667C11.9168 10.2775 10.611 11.5833 9.00016 11.5833C7.38933 11.5833 6.0835 10.2775 6.0835 8.66667C6.0835 7.05584 7.38933 5.75 9.00016 5.75C10.611 5.75 11.9168 7.05584 11.9168 8.66667Z"
      stroke="#4A5353"
      strokeWidth="1.5"
    />
    <path
      d="M9 1.16675C13.0588 1.16675 16.5 4.52756 16.5 8.60489C16.5 12.7471 13.0028 15.654 9.7725 17.6307C9.53709 17.7635 9.27087 17.8334 9 17.8334C8.72913 17.8334 8.46291 17.7635 8.2275 17.6307C5.00325 15.6347 1.5 12.7615 1.5 8.60489C1.5 4.52756 4.9412 1.16675 9 1.16675Z"
      stroke="#4A5353"
      strokeWidth="1.5"
    />
  </svg>
);

const DeviceTender = () => {
  const preLanguage = localStorage.getItem('language');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [language, setLanguage] = useState<any>(null);
  const userId: string = localStorage.getItem('userId') || '';
  const companyId: string = localStorage.getItem('companyId') || '';
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>('');
  const { isMobile } = useResponsive();
  /** The tender whose Locations panel is open. */
  const [locationsFor, setLocationsFor] = useState<TenderRow | null>(null);
  /** Company locations, fetched the first time the panel opens (null until then). */
  const [allLocations, setAllLocations] = useState<LocationOption[] | null>(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [savingLocations, setSavingLocations] = useState<boolean>(false);
  const savingRef = useRef<boolean>(false);
  const locationsSeq = useRef<number>(0);
  const openTenderId = useRef<string>('');

  const emptyState = {
    img: <DeviceIcon />,
    title: language?.['No devices tenders yet'] ?? 'No devices tenders yet',
    desc:
      language?.[
        'Device Tenders define which payment methods (e.g., cash, card, voucher) are accepted on each POS device, ensuring accurate payment handling and simpler reconciliation across locations.'
      ] ??
      'Device Tenders define which payment methods (e.g., cash, card, voucher) are accepted on each POS device, ensuring accurate payment handling and simpler reconciliation across locations.',
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /** One page of tenders (ordered by position server-side); search maps onto `searchValue`. */
  const fetchTenders = async (query: ListQuery) => {
    webLoadingFalse();
    const response: DeviceTenderResponse = await getListDeviceTender(
      query.page,
      query.limit,
      query.search
    );
    return {
      rows: response.data.data.deviceTender ?? [],
      count: response.data.data.count ?? 0,
    };
  };

  const goToTender = (id: string) => webNavigate('/' + preLanguage + '/deviceTender/' + id);

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };
  const handleDeleteTender = async () => {
    try {
      await deleteDeviceTender(idDelete);
      showDeleteToast(language?.['Device Tender Deleted !'] ?? 'Device Tender Deleted !');
    } catch (error) {
      console.error('Error deleting device tender:', error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /* ---------------- Locations panel ---------------- */

  const closeLocations = () => {
    openTenderId.current = '';
    setLocationsFor(null);
  };

  const openLocations = async (row: TenderRow) => {
    openTenderId.current = row.id;
    setLocationsFor(row);
    setSelectedLocationIds((row.location ?? []).map(loc => String(loc.id)));
    if (allLocations !== null) return;
    const seq = ++locationsSeq.current;
    try {
      const response: any = await getLocations(userId, companyId, '', '', '');
      if (seq !== locationsSeq.current) return;
      setAllLocations(
        (response?.data?.data ?? []).map((v: any) => ({ id: String(v.id), name: v.name }))
      );
    } catch (error) {
      if (seq !== locationsSeq.current) return;
      console.error('Error loading locations:', error);
      showApiError(error, { language });
      closeLocations();
    }
  };

  /** Each switch PUTs the whole tender with the new location set, as the old side pane did. */
  const toggleLocation = async (locationId: string) => {
    const row = locationsFor;
    if (!row || savingRef.current) return;
    const previous = selectedLocationIds;
    const next = previous.includes(locationId)
      ? previous.filter(id => id !== locationId)
      : [...previous, locationId];
    savingRef.current = true;
    setSavingLocations(true);
    setSelectedLocationIds(next);
    try {
      const typeValue = TENDER_TYPES.find(v => v.label === row.tenderType)?.value;
      const payload: any = {
        ...row,
        isActive: row.isActive,
        tenderType: typeValue ?? row.tenderType,
        maxPaymentRestriction: +(row.maxPaymentRestriction as any),
        location: next.map(id => ({ id, companyId: companyId })),
      };
      await updateDeviceTender(row.id, payload);
      listRef.current?.reload();
    } catch (error) {
      console.error('Error updating tender locations:', error);
      // Only revert if the same tender's panel is still open.
      if (openTenderId.current === row.id) setSelectedLocationIds(previous);
      showApiError(error, { language });
    } finally {
      savingRef.current = false;
      setSavingLocations(false);
    }
  };

  useEffect(() => {
    if (!locationsFor || isMobile) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLocations();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [locationsFor, isMobile]);

  const mutedLine = (text: string) => (
    <p
      className="font-inter"
      style={{ fontSize: '14px', lineHeight: '20px', color: TEXT_DIM, padding: '12px 0' }}
    >
      {text}
    </p>
  );

  const locationRows =
    allLocations === null
      ? mutedLine(t(language, 'Loading...', 'Loading...'))
      : allLocations.length === 0
        ? mutedLine(t(language, 'No locations found', 'No locations found'))
        : allLocations.map((loc, index) => {
          const on = selectedLocationIds.includes(loc.id);
          return (
            <div
              key={loc.id}
              className="flex items-center justify-between"
              style={{
                gap: '12px',
                minHeight: '48px',
                padding: '8px 0',
                borderBottom:
                  index < allLocations.length - 1 ? `1px solid ${BORDER_SOFT}` : undefined,
              }}
            >
              <p
                className="font-inter"
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '24px',
                  color: TEXT,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {loc.name}
              </p>
              <div
                role="switch"
                aria-checked={on}
                aria-label={loc.name}
                tabIndex={0}
                className={`switch ${on ? 'on' : 'off'}`}
                style={{
                  flex: 'none',
                  opacity: savingLocations ? 0.6 : 1,
                  cursor: savingLocations ? 'progress' : 'pointer',
                }}
                onClick={() => toggleLocation(loc.id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleLocation(loc.id);
                  }
                }}
              >
                <div className={`shadow_toggle slider ${on ? 'move-right' : 'move-left'}`}></div>
              </div>
            </div>
          );
        });

  const locationsTitle = t(language, 'Locations availability', 'Locations availability');

  /* ---------------- List kit configuration ---------------- */

  const typeLabel = (lang: any, type: string) => t(lang, type, type);

  const columns: ListColumn<TenderRow>[] = [
    {
      key: 'icon',
      label: 'Icon',
      width: 64,
      minWidth: 56,
      render: r => <TenderIcon key={r.icon} src={r.icon} />,
      mobile: 'sheet',
    },
    {
      key: 'tenderType',
      label: 'Type',
      width: 120,
      minWidth: 96,
      value: (r, ctx) => typeLabel(ctx.language, r.tenderType),
      render: cells.typePill((r, ctx) => typeLabel(ctx.language, r.tenderType)),
      mobile: 'subtitle',
    },
    { key: 'tenderCode', label: 'Code', width: 110, minWidth: 80, mobile: 'subtitle' },
    {
      key: 'tenderName',
      label: 'Name',
      width: 220,
      hideable: false,
      render: cells.primary(r => r.tenderName),
      mobile: 'title',
      onClick: r => goToTender(String(r.id)),
    },
    {
      key: 'requireReference',
      label: 'Ref',
      width: 80,
      value: (r, ctx) => (r.requireReference ? t(ctx.language, 'Yes', 'Yes') : t(ctx.language, 'No', 'No')),
      mobile: 'sheet',
    },
    { key: 'position', label: 'Position', width: 96, mobile: 'sheet' },
    {
      key: 'updatedAt',
      label: 'Last Update',
      width: 130,
      minWidth: 92,
      render: cells.date(r => r.updatedAt),
      mobile: 'sheet',
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      render: cells.status(r => !!r.isActive),
      mobile: 'trailing',
    },
  ];

  const rowActions: ListRowAction<TenderRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIconV2 />,
      sheetIcon: 'inventory',
      onRun: r => goToTender(String(r.id)),
    },
    {
      key: 'locations',
      label: 'Locations',
      icon: <LocationPinIcon />,
      sheetIcon: 'sliders',
      onRun: r => openLocations(r),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIconV2 />,
      sheetIcon: 'close',
      danger: true,
      onRun: r => handleShowDelete(String(r.id)),
    },
  ];

  return (
    <>
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? 'auto' : undefined,
          paddingBottom: isMobile ? '24px' : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        <div className="lg:flex items-center justify-between">
          <p
            style={{ color: '#222525' }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language?.['Device tender'] ?? 'Device tender'}
          </p>
          <div
            className="cursor-pointer flex items-center justify-center"
            style={{
              gap: '8px',
              padding: '8px 24px 8px 16px',
              backgroundColor: '#0F7679',
              borderRadius: '30px',
              width: 'fit-content',
              marginTop: isMobile ? '20px' : '0px',
            }}
            onClick={() => {
              webNavigate('/' + preLanguage + '/deviceTender/new');
            }}
          >
            <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
              <PlusIconWhite />
            </div>
            <p
              style={{ color: '#FFFFFF' }}
              className="text-[14px] leading-[130%] font-medium font-inter"
            >
              {language?.['Add device tender'] ?? 'Add device tender'}
            </p>
          </div>
        </div>
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchTenders}
          searchPlaceholder="Search device tenders"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="device tenders"
          emptyState={emptyState}
        />
      </div>

      {isMobile ? (
        <BottomSheet open={!!locationsFor} onClose={closeLocations} title={locationsTitle}>
          <div style={{ padding: '0 20px 8px' }}>
            <p
              className="font-inter"
              style={{ fontSize: '13px', color: TEXT_DIM, marginTop: '-6px', marginBottom: '4px' }}
            >
              {locationsFor?.tenderName}
            </p>
            {locationRows}
          </div>
        </BottomSheet>
      ) : (
        locationsFor && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#22252552',
              zIndex: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 16px',
            }}
            onClick={closeLocations}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label={locationsTitle}
              onClick={e => e.stopPropagation()}
              style={{
                padding: '24px',
                borderRadius: '24px',
                backgroundColor: '#FFFFFF',
                width: '530px',
                maxWidth: '100%',
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className="flex items-start justify-between" style={{ gap: '12px' }}>
                <div style={{ minWidth: 0 }}>
                  <p
                    className="font-inter"
                    style={{ fontSize: '20px', fontWeight: 600, lineHeight: '28px', color: TEXT }}
                  >
                    {locationsTitle}
                  </p>
                  <p
                    className="font-inter"
                    style={{ fontSize: '14px', lineHeight: '20px', color: TEXT_DIM }}
                  >
                    {locationsFor.tenderName}
                  </p>
                </div>
                <div
                  onClick={closeLocations}
                  className="flex items-center justify-center cursor-pointer"
                  style={{ width: '24px', height: '24px', flex: 'none' }}
                >
                  <CloseIcon />
                </div>
              </div>
              <div style={{ marginTop: '12px', overflowY: 'auto', minHeight: 0, flex: 1 }}>
                {locationRows}
              </div>
              <div className="flex items-center justify-end" style={{ marginTop: '20px' }}>
                <div
                  onClick={closeLocations}
                  className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                  style={{
                    borderRadius: '30px',
                    padding: '10px 24px',
                    border: '1px solid #C9D7D8',
                    color: '#222525',
                  }}
                >
                  {language?.['Close'] ?? 'Close'}
                </div>
              </div>
            </div>
          </div>
        )
      )}

      <div
        className={`fixed w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        style={{
          backgroundColor: '#22252552',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100%',
        }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            padding: '24px',
            borderRadius: '24px',
            backgroundColor: '#FFFFFF',
            width: '530px',
            maxWidth: 'calc(100vw - 32px)',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
          }}
        >
          <div className="flex items-center justify-between">
            <p
              style={{ fontSize: '20px', color: '#222525' }}
              className="font-semibold font-inter leading-[24px]"
            >
              {language?.['Delete device tender?'] ?? 'Delete device tender?'}
            </p>
            <div
              onClick={() => setShowDelete(false)}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '100%',
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
            <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
              {language?.[
                'This device tender will be permanently deleted. Are you sure you want to continue'
              ] ??
                'This device tender will be permanently deleted. Are you sure you want to continue'}
            </p>
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => setShowDelete(false)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language?.['Cancel'] ?? 'Cancel'}
            </div>
            <div
              onClick={handleDeleteTender}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language?.['Delete'] ?? 'Delete'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeviceTender;
